import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';
import { validateUkPostcode, getAddressesForPostcode } from '../services/postcodeService';
import { addAddress, updateAddress } from '../services/api';

const AddressForm = ({ isOpen, onClose, address, onSave }) => {
  const [formData, setFormData] = useState({
    addressLine1: '',
    addressLine2: '',
    townCity: '',
    postcode: '',
    isDefault: false,
  });
  const [postcodeError, setPostcodeError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isLookingUpPostcode, setIsLookingUpPostcode] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        addressLine1: address.addressLine1 || '',
        addressLine2: address.addressLine2 || '',
        townCity: address.townCity || '',
        postcode: address.postcode || '',
        isDefault: address.isDefault || false,
      });
    } else {
      setFormData({
        addressLine1: '',
        addressLine2: '',
        townCity: '',
        postcode: '',
        isDefault: false,
      });
    }
  }, [address, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value.toUpperCase(),
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePostcodeLookup = async () => {
    if (!formData.postcode) {
      setPostcodeError('Please enter a postcode');
      return;
    }
    if (!validateUkPostcode(formData.postcode)) {
      setPostcodeError('Please enter a valid UK postcode');
      return;
    }
    setPostcodeError('');
    setIsLookingUpPostcode(true);
    try {
      const result = await getAddressesForPostcode(formData.postcode);
      if (result.success) {
        setAddressSuggestions(result.addresses);
      } else {
        setPostcodeError(result.error || 'Failed to find addresses');
      }
    } catch (err) {
      setPostcodeError('An error occurred during postcode lookup');
    } finally {
      setIsLookingUpPostcode(false);
    }
  };

  const handleAddressSelect = (selectedValue) => {
    // Find address by summaryLine or formattedAddress for better matching
    const selected = addressSuggestions.find(a => 
      a.summaryLine === selectedValue || 
      a.formattedAddress === selectedValue ||
      a.line_1 === selectedValue
    );
    if (selected) {
        setFormData(prev => ({
            ...prev,
            addressLine1: selected.line_1 || '',
            addressLine2: selected.line_2 || '',
            townCity: selected.town_or_city || selected.postTown || '',
            postcode: selected.postcode || selected.postCode || prev.postcode
        }));
        setAddressSuggestions([]);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.addressLine1.trim()) errors.addressLine1 = 'Address line 1 is required';
    if (!formData.townCity.trim()) errors.townCity = 'Town/City is required';
    if (!formData.postcode.trim()) errors.postcode = 'Postcode is required';
    else if (!validateUkPostcode(formData.postcode)) errors.postcode = 'Invalid UK postcode';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      // Ensure isDefault is sent as boolean
      const submitData = {
        ...formData,
        isDefault: Boolean(formData.isDefault)
      };
      
      const response = address
        ? await updateAddress(address._id, submitData)
        : await addAddress(submitData);
      if (response.success) {
        onSave(response.addresses || response.data?.addresses);
        onClose();
      } else {
        setFormErrors({ form: response.error || 'Failed to save address' });
      }
    } catch (err) {
      console.error('Address save error:', err);
      setFormErrors({ form: err.response?.data?.error || 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{address ? 'Edit Address' : 'Add New Address'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode</Label>
              <div className="flex gap-2">
                <Input
                  id="postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  placeholder="Enter postcode"
                  className={cn(postcodeError || formErrors.postcode ? 'border-red-500' : '')}
                />
                <Button type="button" onClick={handlePostcodeLookup} disabled={isLookingUpPostcode}>
                  {isLookingUpPostcode ? 'Finding...' : 'Find'}
                </Button>
              </div>
              {(postcodeError || formErrors.postcode) && (
                <p className="text-sm text-red-500">{postcodeError || formErrors.postcode}</p>
              )}
            </div>

            {addressSuggestions.length > 0 && (
              <div className="space-y-2">
                <Label>Select an address ({addressSuggestions.length} found)</Label>
                <Select onValueChange={handleAddressSelect}>
                    <SelectTrigger>
                        <SelectValue placeholder="Select an address" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                        {addressSuggestions.map((addr, index) => {
                          const displayValue = addr.summaryLine || addr.formattedAddress || `${addr.line_1}, ${addr.town_or_city}`;
                          return (
                            <SelectItem key={index} value={displayValue}>
                              {displayValue}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="addressLine1">Address Line 1</Label>
              <Input id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} />
              {formErrors.addressLine1 && <p className="text-sm text-red-500">{formErrors.addressLine1}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
              <Input id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="townCity">Town/City</Label>
              <Input id="townCity" name="townCity" value={formData.townCity} onChange={handleInputChange} />
              {formErrors.townCity && <p className="text-sm text-red-500">{formErrors.townCity}</p>}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="isDefault" name="isDefault" checked={formData.isDefault} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isDefault: checked }))} />
              <Label htmlFor="isDefault">Set as default address</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Address'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddressForm;
